import { requiresRegionCode, type DmFormErrors, type DmPracticeForm } from './dmForms.ts';
import { DmAddressFields } from './DmAddressFields';
import { DmLocaleSelect } from './DmLocaleSelect';
import { DmTextField } from './DmTextField';

interface DmPracticeFormFieldsProps {
    form: DmPracticeForm;
    onChange: (next: DmPracticeForm) => void;
    errors: DmFormErrors;
}

function SubHeading({ children }: { children: string }) {
    return <h3 className="text-[13px] font-bold text-gray-800 pt-1">{children}</h3>;
}

function BillingSameToggle({
    checked,
    onChange
}: {
    checked: boolean;
    onChange: (same: boolean) => void;
}) {
    return (
        <label className="flex items-center gap-2 text-[13px] text-gray-700">
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 accent-gray-900"
            />
            Billing same as shipping
        </label>
    );
}

export function DmPracticeFormFields({ form, onChange, errors }: DmPracticeFormFieldsProps) {
    const text = (key: 'firstname' | 'lastname' | 'phone' | 'countryCode') => ({
        value: form[key],
        onChange: (next: string) => onChange({ ...form, [key]: next }),
        error: errors[key]
    });
    const regionRequired = requiresRegionCode(form.countryCode);
    // Unticking starts the billing address from the current shipping one.
    const toggleBillingSame = (same: boolean) =>
        onChange({
            ...form,
            billingSameAsShipping: same,
            billing: same ? form.billing : form.shipping
        });

    return (
        <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
                <DmTextField label="Contact first name" {...text('firstname')} />
                <DmTextField label="Contact last name" {...text('lastname')} />
                <DmTextField label="Phone" placeholder="+15551234567" {...text('phone')} />
                <DmTextField label="Country code" placeholder="US" {...text('countryCode')} />
                <DmLocaleSelect
                    value={form.locale}
                    onChange={(locale) => onChange({ ...form, locale })}
                />
            </div>
            <SubHeading>Shipping address</SubHeading>
            <DmAddressFields
                prefix="shipping"
                value={form.shipping}
                onChange={(shipping) => onChange({ ...form, shipping })}
                errors={errors}
                regionRequired={regionRequired}
            />
            <BillingSameToggle checked={form.billingSameAsShipping} onChange={toggleBillingSame} />
            {!form.billingSameAsShipping && (
                <>
                    <SubHeading>Billing address</SubHeading>
                    <DmAddressFields
                        prefix="billing"
                        value={form.billing}
                        onChange={(billing) => onChange({ ...form, billing })}
                        errors={errors}
                        regionRequired={regionRequired}
                    />
                </>
            )}
        </div>
    );
}
